import { useEffect, useRef, useState } from 'react';
import { useApp } from '../context.js';
import { isAmount } from '../lib/money.js';

export function Toast() {
    const { toast } = useApp();
    if (!(toast)) return null;
    return (
        <div role="status" aria-live="polite" style={{ bottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }} className={`fixed left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-full shadow-lg animate-fadeIn max-w-[90vw] ${
            toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
            <div className="flex items-center gap-2">
                <span>{toast.type === 'success' ? '✓' : '!'}</span>
                <span className="font-medium">{toast.message}</span>
            </div>
        </div>
    );
}

export function OfflineBar() {
    const { isOnline } = useApp();
    if (!(!isOnline)) return null;
    return (
        <div className="bg-gray-800 text-white text-center py-1 text-xs font-medium">
            You're offline - changes will sync when connected
        </div>
    );
}

export function InstallBanner() {
    const { dismissInstallPrompt, showInstallPrompt, t } = useApp();
    if (!(showInstallPrompt)) return null;
    return (
        <div className={`bg-gradient-to-r ${t.banner} text-white px-4 py-3 shadow-lg animate-fadeIn`}>
            <div className="max-w-lg mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-2xl">📱</span>
                    <div>
                        <p className="font-semibold text-sm">Add to Home Screen</p>
                        <p className="text-xs opacity-90">Get the full app experience!</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={dismissInstallPrompt}
                        className="px-3 py-2.5 text-sm font-medium opacity-80 hover:opacity-100"
                    >
                        Later
                    </button>
                    <button
                        onClick={() => {
                            const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
                            alert(ios
                                ? 'In Safari, tap the Share button (square with an arrow), then "Add to Home Screen".'
                                : 'Open the browser menu (⋮), then "Add to Home screen" or "Install app".');
                            dismissInstallPrompt();
                        }}
                        className={`px-4 py-2.5 bg-white ${t.accentText} rounded-full text-sm font-semibold`}
                    >
                        How?
                    </button>
                </div>
            </div>
        </div>
    );
}

export function GameBanner() {
    const { activeGame, currentProfile, setActiveTab, setExpandedId } = useApp();
    if (!(activeGame && activeGame.status === 'guessing' && !(activeGame.guesses && isAmount(activeGame.guesses[currentProfile])))) return null;
    return (
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-3 shadow-lg animate-fadeIn">
            <div className="max-w-lg mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-2xl">💰</span>
                    <div>
                        <p className="font-semibold text-sm">Guess the Check!</p>
                        <p className="text-xs opacity-90">{activeGame.restaurantName}</p>
                    </div>
                </div>
                <button
                    onClick={() => {
                        // Find and expand the restaurant, then scroll to it
                        setExpandedId(activeGame.restaurantId);
                        setActiveTab('restaurants');
                        // Scroll to the restaurant card after a short delay
                        setTimeout(() => {
                            const element = document.getElementById(`restaurant-${activeGame.restaurantId}`);
                            if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 100);
                    }}
                    className="px-4 py-2 bg-white text-green-600 rounded-full text-sm font-bold apple-button"
                >
                    Join Game
                </button>
            </div>
        </div>
    );
}

// Offline, install and game banners share one fixed stack under the notch (they used to overlap each other),
// and a spacer of the same height keeps the page content from sliding under them.
export function TopBanners() {
    const ref = useRef(null);
    const [height, setHeight] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => setHeight(el.firstChild && el.firstChild.childElementCount ? el.getBoundingClientRect().height : 0));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return (
        <>
            <div ref={ref} className="fixed top-0 left-0 right-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
                <div>
                    <OfflineBar />
                    <InstallBanner />
                    <GameBanner />
                </div>
            </div>
            <div aria-hidden="true" style={{ height }} />
        </>
    );
}
